import { useState, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Textarea,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  Badge,
  Tooltip,
  Box,
  Progress,
  IconButton,
  useToast,
} from '@chakra-ui/react'
import { FiSun, FiCheck, FiX, FiInfo, FiEdit3, FiFolder, FiFileText, FiClock } from 'react-icons/fi'
import { Folder, FolderScope, SmartRenamePrompt } from '@/types'
import apiService from '@/services/api'
import { useAppTranslation } from '@/contexts/LanguageContext'

interface SmartRenamePreview {
  file_id: number
  current_name: string
  suggested_name: string
  reasoning?: string
}

interface SmartRenameJob {
  job_id: string
  status: string
  progress: number
  total_files: number
  processed_files: number
  created_at: string
  completed_at?: string
  error_message?: string
}

interface SmartRenamingModalProps {
  isOpen: boolean
  onClose: () => void
  /** null is the root of the tree — the dashboard's "All Documents" view. */
  folder: Folder | null
  /** Explorer selection frozen when the modal opened. Empty means "everything in scope". */
  selectedFileIds?: number[]
  /** Selected files left out because their text had not been extracted yet. */
  skippedCount?: number
  folderFileCount?: number
  onRenamingComplete: () => void
}

export default function SmartRenamingModal({
  isOpen,
  onClose,
  folder,
  selectedFileIds,
  skippedCount = 0,
  folderFileCount,
  onRenamingComplete,
}: SmartRenamingModalProps) {
  const [instructions, setInstructions] = useState('')
  const [previews, setPreviews] = useState<SmartRenamePreview[]>([])
  const [selectedFiles, setSelectedFiles] = useState<Set<number>>(new Set())
  const [isGenerating, setIsGenerating] = useState(false)
  const [isRenaming, setIsRenaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeJob, setActiveJob] = useState<SmartRenameJob | null>(null)
  const [jobPollingInterval, setJobPollingInterval] = useState<NodeJS.Timeout | null>(null)
  const [recentPrompts, setRecentPrompts] = useState<SmartRenamePrompt[]>([])
  const toast = useToast()
  const { t } = useAppTranslation(['files', 'common'])

  // The API addresses the top of the tree as the literal 'root', so a missing folder is not
  // "no scope" here — it is the files that live outside every folder.
  const scope: FolderScope = folder?.id ?? 'root'
  const scopeName = folder?.name ?? t('fileExplorer.allDocuments', { ns: 'files' })

  const isScoped = (selectedFileIds?.length ?? 0) > 0
  // A restored plan is folder-wide, so it can cover files outside the current selection.
  const hasStaleRestoredResults =
    isScoped && previews.length > 0 && !previews.every((p) => selectedFileIds!.includes(p.file_id))

  // Dynamic example instructions
  const EXAMPLE_INSTRUCTIONS = [
    t('smartRename.exampleSequential', { ns: 'files' }),
    t('smartRename.exampleDate', { ns: 'files' }),
    t('smartRename.exampleCategory', { ns: 'files' }),
    t('smartRename.exampleCleanup', { ns: 'files' }),
    t('smartRename.exampleDescriptive', { ns: 'files' }),
    t('smartRename.exampleInvoices', { ns: 'files' }),
  ]

  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setInstructions('')
      setPreviews([])
      setSelectedFiles(new Set())
      setError(null)
      setActiveJob(null)
      setRecentPrompts([])

      // Clear any existing polling interval
      if (jobPollingInterval) {
        clearInterval(jobPollingInterval)
        setJobPollingInterval(null)
      }

      // Check for existing jobs for this folder
      checkExistingJobs()
      loadRecentPrompts()
    } else {
      // Clear polling interval when modal closes
      if (jobPollingInterval) {
        clearInterval(jobPollingInterval)
        setJobPollingInterval(null)
      }
    }
  }, [isOpen])

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (jobPollingInterval) {
        clearInterval(jobPollingInterval)
      }
    }
  }, [jobPollingInterval])

  const checkExistingJobs = async () => {
    try {
      const response = await apiService.getFolderSmartRenameJobs(scope)
      const activeJobData = response.jobs.find(
        (job) => job.status === 'pending' || job.status === 'processing'
      )

      if (activeJobData) {
        setActiveJob({
          job_id: activeJobData.job_id,
          status: activeJobData.status,
          progress: activeJobData.progress,
          total_files: activeJobData.total_files,
          processed_files: activeJobData.processed_files,
          created_at: activeJobData.created_at,
          completed_at: activeJobData.completed_at,
        })
        setInstructions(activeJobData.instructions)
        startJobPolling(activeJobData.job_id)
      } else if (response.latest_completed?.results) {
        // Show latest completed results
        setPreviews(response.latest_completed.results)
        setInstructions(response.latest_completed.instructions || '')
        const allFileIds = new Set(
          response.latest_completed.results.map((p: SmartRenamePreview) => p.file_id)
        ) as Set<number>
        setSelectedFiles(allFileIds)
      }
    } catch (error) {
      console.error('Failed to check existing jobs:', error)
    }
  }

  const loadRecentPrompts = async () => {
    try {
      setRecentPrompts(await apiService.getSmartRenamePrompts(scope))
    } catch (error) {
      // History is a convenience: a failure here must not get in the way of renaming
      console.error('Failed to load recent prompts:', error)
    }
  }

  const handleRemovePrompt = async (promptId: number) => {
    const previous = recentPrompts
    setRecentPrompts((prompts) => prompts.filter((prompt) => prompt.id !== promptId))

    try {
      await apiService.deleteSmartRenamePrompt(scope, promptId)
    } catch (error) {
      console.error('Failed to remove prompt:', error)
      setRecentPrompts(previous)
      toast({
        title: t('smartRename.removePromptFailed', { ns: 'files' }),
        status: 'error',
        duration: 3000,
      })
    }
  }

  const startJobPolling = (jobId: string) => {
    const interval = setInterval(async () => {
      try {
        const status = await apiService.getSmartRenameJobStatus(jobId)
        setActiveJob((prev) => (prev ? { ...prev, ...status } : null))

        if (status.status === 'completed') {
          // Job completed, get results
          try {
            const results = await apiService.getSmartRenameJobResults(jobId)
            setPreviews(results.suggestions)
            const allFileIds = new Set(results.suggestions.map((p) => p.file_id))
            setSelectedFiles(allFileIds)
            setActiveJob(null)
            clearInterval(interval)
            setJobPollingInterval(null)

            toast({
              title: t('smartRename.jobCompleted', { ns: 'files' }),
              description: t('smartRename.jobCompletedDescription', {
                ns: 'files',
                count: results.suggestions.length,
              }),
              status: 'success',
              duration: 5000,
            })
          } catch (error) {
            console.error('Failed to get job results:', error)
            setError('Failed to get rename suggestions')
            setActiveJob(null)
            clearInterval(interval)
            setJobPollingInterval(null)
          }
        } else if (status.status === 'failed') {
          setError(status.error_message || 'Job failed')
          setActiveJob(null)
          clearInterval(interval)
          setJobPollingInterval(null)
        } else if (status.status === 'cancelled') {
          setActiveJob(null)
          clearInterval(interval)
          setJobPollingInterval(null)
        }
      } catch (error) {
        console.error('Job polling error:', error)
        clearInterval(interval)
        setJobPollingInterval(null)
        setActiveJob(null)
      }
    }, 2000) // Poll every 2 seconds

    setJobPollingInterval(interval)
  }

  const handleGeneratePreviews = async () => {
    if (!instructions.trim()) {
      setError(t('smartRename.provideInstructions', { ns: 'files' }))
      return
    }

    setIsGenerating(true)
    setError(null)
    setPreviews([])
    setSelectedFiles(new Set())

    try {
      // Let the backend decide async vs sync based on file count
      const response = await apiService.generateSmartRenames(
        scope,
        instructions.trim(),
        true,
        isScoped ? selectedFileIds : undefined
      )

      // The backend records the prompt as part of the run, so re-read the history
      loadRecentPrompts()

      if (response.async && response.job_id) {
        // Async processing - start polling for job status
        setActiveJob({
          job_id: response.job_id,
          status: response.status || 'pending',
          progress: 0,
          total_files: response.total_files || 0,
          processed_files: 0,
          created_at: new Date().toISOString(),
        })
        startJobPolling(response.job_id)

        toast({
          title: t('smartRename.jobStarted', { ns: 'files' }),
          description: t('smartRename.jobStartedDescription', {
            ns: 'files',
            count: response.total_files || 0,
          }),
          status: 'info',
          duration: 5000,
        })
      } else if (response.suggestions) {
        // Synchronous processing - show results immediately
        setPreviews(response.suggestions)
        const allFileIds = new Set(response.suggestions.map((p) => p.file_id))
        setSelectedFiles(allFileIds)

        if (response.suggestions.length === 0) {
          setError(t('smartRename.noSuggestions', { ns: 'files' }))
        }
      }
    } catch (err: any) {
      console.error(err)
      setError(
        err.response?.data?.error || err.message || t('smartRename.generateError', { ns: 'files' })
      )
    } finally {
      setIsGenerating(false)
    }
  }

  const handleCancelJob = async () => {
    if (!activeJob) return

    try {
      await apiService.cancelSmartRenameJob(activeJob.job_id)
      setActiveJob(null)
      if (jobPollingInterval) {
        clearInterval(jobPollingInterval)
        setJobPollingInterval(null)
      }

      toast({
        title: t('smartRename.jobCancelled', { ns: 'files' }),
        status: 'info',
        duration: 3000,
      })
    } catch (error) {
      console.error('Failed to cancel job:', error)
      setError('Failed to cancel job')
    }
  }

  const handleFileToggle = (fileId: number) => {
    const newSelected = new Set(selectedFiles)
    if (newSelected.has(fileId)) {
      newSelected.delete(fileId)
    } else {
      newSelected.add(fileId)
    }
    setSelectedFiles(newSelected)
  }

  const handleSelectAll = () => {
    if (selectedFiles.size === previews.length) {
      setSelectedFiles(new Set())
    } else {
      setSelectedFiles(new Set(previews.map((p) => p.file_id)))
    }
  }

  const handleApplyRenames = async () => {
    if (selectedFiles.size === 0) {
      setError(t('smartRename.selectAtLeastOne', { ns: 'files' }))
      return
    }

    setIsRenaming(true)
    setError(null)

    try {
      const updates = previews
        .filter((p) => selectedFiles.has(p.file_id))
        .map((p) => ({
          file_id: p.file_id,
          new_name: p.suggested_name,
        }))

      const result = await apiService.bulkRenameFiles(updates)

      toast({
        title: t('smartRename.renameSuccess', { ns: 'files' }),
        description: [
          t('smartRename.renameSuccessDescription', { ns: 'files', count: result.success }),
          result.errors.length > 0 &&
            t('smartRename.renameErrors', { ns: 'files', count: result.errors.length }),
        ]
          .filter(Boolean)
          .join(', '),
        status: result.errors.length === 0 ? 'success' : 'warning',
        duration: 3000,
      })

      if (result.errors.length > 0) {
        console.error('Rename errors:', result.errors)
      }

      // Auto-clear smart rename jobs after rename application
      // Clear suggestions regardless of partial errors since user has taken action
      try {
        await apiService.clearFolderSmartRenameJobs(scope)
      } catch (clearError) {
        console.error('Failed to clear smart rename jobs:', clearError)
        // Don't show error to user as this is a cleanup operation
      }

      onRenamingComplete()
      onClose()
    } catch (err: any) {
      setError(
        err.response?.data?.error || err.message || t('smartRename.renameError', { ns: 'files' })
      )
    } finally {
      setIsRenaming(false)
    }
  }

  const handleUseExample = (example: string) => {
    setInstructions(example)
  }

  const handleDiscardSuggestions = async () => {
    try {
      // Clear backend job history
      await apiService.clearFolderSmartRenameJobs(scope)

      // Clear local state
      setPreviews([])
      setSelectedFiles(new Set())
      setInstructions('')
      setError(null)

      toast({
        title: 'Suggestions Discarded',
        description: 'Previous smart rename suggestions have been cleared.',
        status: 'info',
        duration: 3000,
      })
    } catch (error) {
      console.error('Failed to clear smart rename jobs:', error)
      setError('Failed to discard suggestions')
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent maxW="90vw" maxH="90vh" borderRadius="lg" boxShadow="xl" overflow="hidden">
        <ModalHeader
          bgGradient="linear(to-r, purple.500, pink.600)"
          color="white"
          py={6}
          borderRadius="lg lg 0 0"
        >
          <HStack spacing={3}>
            <FiSun size="24px" />
            <Text fontSize="xl" fontWeight="bold">
              {t('smartRename.title', { ns: 'files' })}
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton color="white" size="lg" top={4} right={4} />

        <ModalBody overflow="auto" p={6} bg="gray.50">
          <VStack spacing={6} align="stretch">
            {/* Folder Info */}
            <Box p={4} bg="purple.25" borderRadius="lg" border="1px solid" borderColor="purple.100">
              <HStack spacing={3}>
                <Box p={2} bg="purple.100" borderRadius="md" color="purple.600">
                  <FiFolder size="20px" />
                </Box>
                <VStack align="start" spacing={1}>
                  <Text fontSize="md" fontWeight="semibold" color="purple.800">
                    {scopeName}
                  </Text>
                  <Text fontSize="sm" color="purple.600">
                    {isScoped
                      ? t('smartRename.scopeSelected', {
                          ns: 'files',
                          count: selectedFileIds!.length,
                        })
                      : folderFileCount != null
                        ? t(
                            folder ? 'smartRename.scopeAllInFolder' : 'smartRename.scopeAllAtRoot',
                            { ns: 'files', count: folderFileCount }
                          )
                        : t('smartRename.description', { ns: 'files' })}
                  </Text>
                </VStack>
              </HStack>
            </Box>

            {hasStaleRestoredResults && (
              <Alert status="info" borderRadius="lg">
                <AlertIcon />
                <Text fontSize="sm">{t('smartRename.scopeStaleResults', { ns: 'files' })}</Text>
              </Alert>
            )}

            {skippedCount > 0 && (
              <Alert status="info" borderRadius="lg">
                <AlertIcon />
                <Text fontSize="sm">
                  {t('smartRename.someSkipped', { ns: 'files', count: skippedCount })}
                </Text>
              </Alert>
            )}

            {/* Instructions Input */}
            <Box
              p={5}
              bg="white"
              borderRadius="lg"
              border="1px solid"
              borderColor="gray.200"
              boxShadow="sm"
            >
              <VStack spacing={4} align="stretch">
                <Text fontWeight="semibold" fontSize="lg" color="gray.800">
                  {t('smartRename.instructionsTitle', { ns: 'files' })}
                </Text>
                <Textarea
                  placeholder={t('smartRename.instructionsPlaceholder', { ns: 'files' })}
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  rows={3}
                  isDisabled={isGenerating}
                  borderRadius="lg"
                  border="2px solid"
                  borderColor="gray.200"
                  _focus={{
                    borderColor: 'purple.400',
                    boxShadow: '0 0 0 1px rgba(159, 122, 234, 0.3)',
                  }}
                  _hover={{
                    borderColor: 'gray.300',
                  }}
                  fontSize="md"
                  resize="vertical"
                />

                {/* Prompts already used on this folder */}
                {recentPrompts.length > 0 && (
                  <Box
                    p={3}
                    bg="gray.50"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    <Text fontSize="sm" fontWeight="medium" mb={3} color="gray.700">
                      {t(
                        folder
                          ? 'smartRename.recentPromptsHeader'
                          : 'smartRename.recentPromptsHeaderRoot',
                        { ns: 'files' }
                      )}
                    </Text>
                    <VStack spacing={2} align="stretch">
                      {recentPrompts.map((recent) => (
                        <HStack
                          key={recent.id}
                          p={2}
                          bg="white"
                          borderRadius="md"
                          border="1px solid"
                          borderColor="gray.200"
                          cursor="pointer"
                          _hover={{
                            borderColor: 'purple.300',
                            bg: 'purple.25',
                            transform: 'translateY(-1px)',
                          }}
                          transition="all 0.2s ease"
                          onClick={() => handleUseExample(recent.prompt)}
                          align="start"
                          spacing={2}
                        >
                          <Box flex={1} minW={0}>
                            <Text
                              fontSize="sm"
                              color="purple.600"
                              fontWeight="medium"
                              noOfLines={2}
                            >
                              {recent.prompt}
                            </Text>
                            <Text fontSize="xs" color="gray.500" mt={1}>
                              {new Date(recent.used_at).toLocaleString()}
                            </Text>
                          </Box>
                          <Tooltip label={t('smartRename.removePrompt', { ns: 'files' })}>
                            <IconButton
                              aria-label={t('smartRename.removePrompt', { ns: 'files' })}
                              icon={<FiX />}
                              size="xs"
                              variant="ghost"
                              color="gray.500"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleRemovePrompt(recent.id)
                              }}
                            />
                          </Tooltip>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                )}

                {/* Example Instructions */}
                <Box p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                  <Text fontSize="sm" fontWeight="medium" mb={3} color="gray.700">
                    {t('smartRename.exampleInstructionsHeader', { ns: 'files' })}
                  </Text>
                  <VStack spacing={2} align="stretch">
                    {EXAMPLE_INSTRUCTIONS.map((example, index) => (
                      <Box
                        key={index}
                        p={2}
                        bg="white"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="gray.200"
                        cursor="pointer"
                        _hover={{
                          borderColor: 'purple.300',
                          bg: 'purple.25',
                          transform: 'translateY(-1px)',
                        }}
                        transition="all 0.2s ease"
                        onClick={() => handleUseExample(example)}
                      >
                        <Text fontSize="sm" color="purple.600" fontWeight="medium">
                          {example}
                        </Text>
                      </Box>
                    ))}
                  </VStack>
                </Box>

                <Button
                  colorScheme="purple"
                  onClick={handleGeneratePreviews}
                  isLoading={isGenerating}
                  loadingText={t('smartRename.analyzing', { ns: 'files' })}
                  leftIcon={<FiEdit3 />}
                  isDisabled={!instructions.trim() || !!activeJob}
                  borderRadius="lg"
                  py={3}
                  h="auto"
                  fontWeight="semibold"
                  fontSize="md"
                  _hover={{
                    transform: 'translateY(-1px)',
                    boxShadow: 'lg',
                  }}
                  transition="all 0.2s"
                >
                  {activeJob
                    ? t('smartRename.jobInProgress', { ns: 'files' })
                    : t('smartRename.generateSuggestions', { ns: 'files' })}
                </Button>
              </VStack>
            </Box>

            {/* Error Display */}
            {error && (
              <Alert status="error">
                <AlertIcon />
                <Text>{error}</Text>
              </Alert>
            )}

            {/* Job Progress Display */}
            {activeJob && (
              <Box
                p={5}
                bg="white"
                borderRadius="lg"
                border="1px solid"
                borderColor="blue.200"
                boxShadow="sm"
              >
                <VStack spacing={4} align="stretch">
                  <HStack justify="space-between">
                    <HStack spacing={3}>
                      <Box p={2} bg="blue.100" borderRadius="md" color="blue.600">
                        <FiClock size="20px" />
                      </Box>
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="bold" fontSize="lg" color="blue.800">
                          {activeJob.status === 'pending' &&
                            t('smartRename.jobPending', { ns: 'files' })}
                          {activeJob.status === 'processing' &&
                            t('smartRename.jobProcessing', { ns: 'files' })}
                        </Text>
                        <Text fontSize="sm" color="blue.600">
                          {t('smartRename.processingFiles', {
                            ns: 'files',
                            processed: activeJob.processed_files,
                            total: activeJob.total_files,
                          })}
                        </Text>
                      </VStack>
                    </HStack>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="red"
                      onClick={handleCancelJob}
                      leftIcon={<FiX />}
                    >
                      {t('buttons.cancel', { ns: 'common' })}
                    </Button>
                  </HStack>

                  <Box>
                    <HStack justify="space-between" mb={2}>
                      <Text fontSize="sm" fontWeight="medium" color="gray.700">
                        {t('smartRename.progress', { ns: 'files' })}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {activeJob.progress}%
                      </Text>
                    </HStack>
                    <Progress
                      value={activeJob.progress}
                      colorScheme="blue"
                      borderRadius="full"
                      size="lg"
                      hasStripe
                      isAnimated
                    />
                  </Box>

                  <Text fontSize="xs" color="gray.500" textAlign="center">
                    {t('smartRename.jobStartedAt', {
                      ns: 'files',
                      time: new Date(activeJob.created_at).toLocaleTimeString(),
                    })}
                  </Text>
                </VStack>
              </Box>
            )}

            {/* Preview Table */}
            {previews.length > 0 && (
              <Box
                p={5}
                bg="white"
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
                boxShadow="sm"
              >
                <VStack spacing={4} align="stretch">
                  <HStack justify="space-between">
                    <HStack spacing={3}>
                      <FiFileText size="20px" color="purple" />
                      <Text fontWeight="bold" fontSize="lg" color="gray.800">
                        {t('smartRename.renamePreview', { ns: 'files' })}
                      </Text>
                      <Badge colorScheme="purple" variant="solid" borderRadius="full" px={3} py={1}>
                        {t('plurals.file', { ns: 'common', count: previews.length })}
                      </Badge>
                    </HStack>
                    <HStack spacing={3}>
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">
                        {t('smartRename.selected', { ns: 'files', count: selectedFiles.size })}
                      </Text>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleSelectAll}
                        borderRadius="lg"
                        colorScheme="purple"
                        _hover={{ transform: 'translateY(-1px)' }}
                        transition="all 0.2s"
                      >
                        {selectedFiles.size === previews.length
                          ? t('buttons.deselectAll', { ns: 'common' })
                          : t('buttons.selectAll', { ns: 'common' })}
                      </Button>
                    </HStack>
                  </HStack>

                  <Box
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="gray.200"
                    overflow="hidden"
                  >
                    <Table size="sm">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th width="40px" py={3}>
                            <Checkbox
                              isChecked={
                                selectedFiles.size === previews.length && previews.length > 0
                              }
                              isIndeterminate={
                                selectedFiles.size > 0 && selectedFiles.size < previews.length
                              }
                              onChange={handleSelectAll}
                              colorScheme="purple"
                            />
                          </Th>
                          <Th fontWeight="bold" color="gray.700" py={3} width="45%">
                            {t('smartRename.currentName', { ns: 'files' })}
                          </Th>
                          <Th fontWeight="bold" color="gray.700" py={3} width="55%">
                            {t('smartRename.suggestedName', { ns: 'files' })}
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {previews.map((preview, index) => {
                          const isSelected = selectedFiles.has(preview.file_id)
                          const hasChanged = preview.suggested_name !== preview.current_name

                          return (
                            <Tr
                              key={preview.file_id}
                              bg={isSelected ? 'purple.25' : 'white'}
                              _hover={{ bg: isSelected ? 'purple.50' : 'gray.25' }}
                              transition="all 0.2s"
                              borderBottom={index < previews.length - 1 ? '1px solid' : 'none'}
                              borderColor="gray.200"
                            >
                              <Td py={3}>
                                <Checkbox
                                  isChecked={isSelected}
                                  onChange={() => handleFileToggle(preview.file_id)}
                                  colorScheme="purple"
                                />
                              </Td>
                              <Td py={3}>
                                <Text
                                  fontSize="sm"
                                  fontFamily="mono"
                                  color="gray.600"
                                  bg="gray.100"
                                  px={3}
                                  py={2}
                                  borderRadius="md"
                                  wordBreak="break-word"
                                >
                                  {preview.current_name}
                                </Text>
                              </Td>
                              <Td py={3}>
                                <HStack spacing={2} align="center">
                                  <Text
                                    fontSize="sm"
                                    fontFamily="mono"
                                    fontWeight="medium"
                                    color={hasChanged ? 'purple.600' : 'gray.500'}
                                    bg={hasChanged ? 'purple.50' : 'gray.100'}
                                    px={3}
                                    py={2}
                                    borderRadius="md"
                                    wordBreak="break-word"
                                    flex={1}
                                  >
                                    {preview.suggested_name}
                                  </Text>
                                  <HStack spacing={1}>
                                    {preview.reasoning && (
                                      <Tooltip
                                        label={preview.reasoning}
                                        placement="top"
                                        hasArrow
                                        bg="gray.700"
                                        color="white"
                                        fontSize="sm"
                                        p={3}
                                        borderRadius="md"
                                        maxW="300px"
                                      >
                                        <Box
                                          cursor="help"
                                          color="blue.500"
                                          _hover={{ color: 'blue.600' }}
                                          transition="color 0.2s"
                                        >
                                          <FiInfo size="16px" />
                                        </Box>
                                      </Tooltip>
                                    )}
                                    {hasChanged && (
                                      <Badge
                                        colorScheme="green"
                                        variant="solid"
                                        borderRadius="full"
                                        fontSize="xs"
                                      >
                                        {t('smartRename.changed', { ns: 'files' })}
                                      </Badge>
                                    )}
                                  </HStack>
                                </HStack>
                              </Td>
                            </Tr>
                          )
                        })}
                      </Tbody>
                    </Table>
                  </Box>
                </VStack>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter bg="white" borderTop="1px solid" borderColor="gray.200" py={4}>
          <HStack spacing={3} w="full" justify="space-between">
            <Text fontSize="sm" color="gray.600">
              {previews.length > 0
                ? t('smartRename.filesSelectedCount', {
                    ns: 'files',
                    selected: selectedFiles.size,
                    total: previews.length,
                  })
                : t('smartRename.generateToPreview', { ns: 'files' })}
            </Text>
            <HStack spacing={3}>
              <Button
                variant="ghost"
                onClick={onClose}
                isDisabled={isRenaming}
                borderRadius="lg"
                px={6}
                _hover={{ bg: 'gray.100' }}
              >
                {t('buttons.cancel', { ns: 'common' })}
              </Button>
              {previews.length > 0 && (
                <Button
                  variant="outline"
                  colorScheme="red"
                  onClick={handleDiscardSuggestions}
                  isDisabled={isRenaming || !!activeJob}
                  borderRadius="lg"
                  px={6}
                  leftIcon={<FiX />}
                  _hover={{ bg: 'red.50' }}
                >
                  Discard Suggestions
                </Button>
              )}
              {previews.length > 0 && (
                <Button
                  colorScheme="purple"
                  onClick={handleApplyRenames}
                  isLoading={isRenaming}
                  loadingText={t('smartRename.renamingFiles', { ns: 'files' })}
                  leftIcon={<FiCheck />}
                  isDisabled={selectedFiles.size === 0}
                  borderRadius="lg"
                  px={6}
                  py={3}
                  h="auto"
                  fontWeight="semibold"
                  _hover={{
                    transform: 'translateY(-1px)',
                    boxShadow: 'lg',
                  }}
                  transition="all 0.2s"
                >
                  {t('smartRename.applyChanges', { ns: 'files', count: selectedFiles.size })}
                </Button>
              )}
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
